import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import openNotification from "../../components/Common/OpenNotification";
import axiosInstance from "../../utils/axiosInstance";
import { DELETE_GUIDELINE, GET_ALL_GUIDELINES, GET_ALL_GUIDELINES_ADMIN, UPLOAD_GUIDELINE_FILE } from "../../api/endpoints";

const initialState = {
    guidelines: [],
    loading: false,
};

export const getAllGuidelines = createAsyncThunk(
    "guideline/getAllGuidelines",
    async (params = {}, { rejectWithValue }) => {
        try {
            const role = sessionStorage.getItem('role')?.toLowerCase();
            const url = role === 'admin' ? GET_ALL_GUIDELINES_ADMIN : GET_ALL_GUIDELINES;
            const response = await axiosInstance.get(url, {
                params: role === 'admin' && params.MNF_UNIT ? { MNF_UNIT: params.MNF_UNIT } : undefined,
            });
            return response.data;
        } catch (error) {
            const message = error?.response?.data?.message || 'Something went wrong.';
            openNotification('error', 'Failed to Load Files', message);
            return rejectWithValue(message);
        }
    }
);

export const uploadGuidelineFile = createAsyncThunk(
    "guideline/uploadGuidelineFile",
    async ({ file, mnfUnit, refetchMnfUnit } = {}, { dispatch, rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("file", file); // Assumes your backend expects `file` as key

            const response = await axiosInstance.post(UPLOAD_GUIDELINE_FILE, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                params: mnfUnit ? { MNF_UNIT: mnfUnit } : undefined,
            });

            openNotification('success', 'Upload Successful', response?.data?.message || 'File uploaded successfully.');
            dispatch(getAllGuidelines({ MNF_UNIT: refetchMnfUnit }));
            return response.data;
        } catch (error) {
            const message = error?.response?.data?.message || 'Failed to upload file.';
            openNotification('error', 'Upload Failed', message);
            return rejectWithValue(message);
        }
    }
);

export const deleteGuideline = createAsyncThunk(
    "guideline/deleteGuideline",
    async ({ id, mnfUnit } = {}, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(`${DELETE_GUIDELINE}/${id}`);
            openNotification('success', 'File Deleted', response?.data?.message || 'File deleted successfully.');
            dispatch(getAllGuidelines({ MNF_UNIT: mnfUnit }));
            return response.data;
        } catch (error) {
            const message = error?.response?.data?.message || 'Failed to delete.';
            openNotification('error', 'Delete Failed', message);
            return rejectWithValue(message);
        }
    }
);


const guidelineSlice = createSlice({
    name: "guideline",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Reducer for fetch guidelines
            .addCase(getAllGuidelines.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllGuidelines.fulfilled, (state, action) => {
                state.loading = false;
                state.guidelines = action.payload.data;
            })
            .addCase(getAllGuidelines.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // upload file cases
            .addCase(uploadGuidelineFile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(uploadGuidelineFile.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(uploadGuidelineFile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // delete file cases
            .addCase(deleteGuideline.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteGuideline.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(deleteGuideline.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
    },
});

export default guidelineSlice.reducer;
